/**
 * DateTimePicker — 날짜/시간 선택 dropdown
 *
 * ⚠️ 모양/레이아웃 동결 (BugHerd #19, 2026-05-08 클라 합의)
 * ─────────────────────────────────────────────────────────
 * 클라 컴플레인 이력으로 합의된 동작:
 *
 *   📌 모바일: dropdown 이 navbar 바로 아래(top: 64px) 에서 시작,
 *      좌우 8px inset, 컨텐츠 길이만큼 펼쳐짐. 큰 폰에서는 스크롤 0,
 *      작은 폰(SE 등) 에서만 안전망으로 모달 안 overflow-y-auto.
 *   📌 데스크톱: input 우측 정렬 absolute dropdown (sm:w-[460px]).
 *   📌 헤더 다국어: en "May 2026" / es "mayo de 2026" /
 *      ja·zh "2026年 5月" (년-월 사이 띄어쓰기 필수) / ko "2026년 5월".
 *
 * 임의로 fixed→absolute, top 값, max-height, calendar cell 크기,
 * TimeColumn VISIBLE 등 변경하지 마세요. 회귀 시 클라 즉시 컴플레인.
 * 변경 필요 시 BugHerd 티켓 발행 + PR 리뷰 필수.
 * ─────────────────────────────────────────────────────────
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';
import client from '../../api/client';
import endpoints from '../../api/endpoints';

// 모바일 dropdown 시작 y-위치 (navbar 높이). 메모: Navbar 는 sticky top-0 z-50, 59px.
// 약간 여유 두고 64px 로 고정.
const MOBILE_DROPDOWN_TOP = 64;

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = [0, 10, 20, 30, 40, 50];

function TimeColumn({ items, value, onChange, label }) {
  // BugHerd #19 후속: 모바일에서 달력 + 시간 wheel + Confirm 이 한 화면에 다 들어가야 함.
  // VISIBLE 4 → 3 (height 84px) 으로 줄여 calendar 영역과 함께 viewport 안에 들어감.
  const ref = useRef(null);
  const ITEM_H = 28;
  const VISIBLE = 3;
  const HEIGHT = ITEM_H * VISIBLE;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const idx = items.indexOf(value);
    if (idx >= 0) {
      el.scrollTo({ top: idx * ITEM_H, behavior: 'auto' });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClick = (item) => {
    onChange(item);
    const el = ref.current;
    if (!el) return;
    const idx = items.indexOf(item);
    el.scrollTo({ top: idx * ITEM_H, behavior: 'smooth' });
  };

  return (
    <div className="flex flex-col items-center gap-1">
      <span className="text-2xs font-bold text-mono-400">{label}</span>
      <div
        ref={ref}
        className="overflow-y-auto scrollbar-hide rounded-lg border border-mono-100 bg-mono-50/50"
        style={{ width: 52, height: HEIGHT }}
      >
        {items.map((item) => (
          <button
            key={item}
            type="button"
            onClick={() => handleClick(item)}
            className={`w-full flex items-center justify-center text-2sm transition
              ${value === item
                ? 'bg-orange-500 text-white font-bold rounded'
                : 'text-mono-600 hover:bg-orange-50 font-medium'
              }
            `}
            style={{ height: ITEM_H }}
          >
            {pad(item)}
          </button>
        ))}
      </div>
    </div>
  );
}

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function generateCalendarDays(year, month, allowPast = false) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = getDaysInMonth(year, month);
  const daysInPrev = month === 0 ? getDaysInMonth(year - 1, 11) : getDaysInMonth(year, month - 1);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const days = [];

  // Previous month filler
  for (let i = firstDay - 1; i >= 0; i--) {
    const d = daysInPrev - i;
    const date = new Date(year, month - 1, d);
    days.push({ date, day: d, isCurrentMonth: false, isPast: !allowPast && date < today });
  }

  // Current month — 당일은 현재시간+2시간(120분) 버퍼 기준으로 예약 가능 시간 없으면 비활성화
  // latestBookableMinute을 23:59로 두어 22:00 직전까지는 당일 예약 가능 (이전엔 23:30이라 21:30 이후 모든 당일 비활성화되는 부수 이슈 있었음)
  const nowForBuffer = new Date();
  const bufferMinutes = 120;
  const latestBookableMinute = 23 * 60 + 59; // 23:59 — 22:00 이전까지 당일 예약 가능
  const todayNoTime = nowForBuffer.getHours() * 60 + nowForBuffer.getMinutes() + bufferMinutes > latestBookableMinute;

  for (let d = 1; d <= daysInMonth; d++) {
    const date = new Date(year, month, d);
    const isToday = date.getTime() === today.getTime();
    const past = allowPast ? false : (date < today || (isToday && todayNoTime));
    days.push({ date, day: d, isCurrentMonth: true, isPast: past });
  }

  // Next month filler (fill to 42 = 6 rows)
  const remaining = 42 - days.length;
  for (let d = 1; d <= remaining; d++) {
    const date = new Date(year, month + 1, d);
    days.push({ date, day: d, isCurrentMonth: false, isPast: false });
  }

  return days;
}

function pad(n) {
  return String(n).padStart(2, '0');
}

function formatDisplay(date, hour, minute, t) {
  if (!date) return '';
  const y = date.getFullYear();
  const m = date.getMonth() + 1;
  const d = date.getDate();
  return t('common.datetime_full_format', {
    year: y,
    month: pad(m),
    day: pad(d),
    hour: pad(hour),
    minute: pad(minute),
  });
}

function toISOString(date, hour, minute) {
  if (!date) return '';
  const y = date.getFullYear();
  const m = pad(date.getMonth() + 1);
  const d = pad(date.getDate());
  return `${y}-${m}-${d}T${pad(hour)}:${pad(minute)}`;
}

function parseISO(val) {
  if (!val) return { date: null, hour: 9, minute: 0 };
  const [datePart, timePart] = val.split('T');
  const [y, m, d] = datePart.split('-').map(Number);
  const [h, min] = (timePart || '09:00').split(':').map(Number);
  return { date: new Date(y, m - 1, d), hour: h, minute: min };
}

export default function DateTimePicker({ value, onChange, placeholder, className = '', maxDateTime = '', maxMonths = 0, dateOnly = false, allowPast = false, notice = '', blockHolidays = false }) {
  // dateOnly=true 면 시간 wheel + 헤더 "이용 시작 시간" 숨김. onChange 는 'YYYY-MM-DD' 만 emit.
  // 어드민 공휴일(종일) 등 시간 의미 없는 케이스에 시간대 휴무와 동일한 UI 통일 위해 사용.
  // allowPast=true 면 과거 날짜 선택 가능 + 2h/미래 알림 가드 스킵. 어드민 검색 필터(이용일 from/to)용.
  const { t, i18n } = useTranslation();

  // BugHerd #19 후속: 다국어 헤더 표기 자연화
  // - en: "May 2026" / es: "mayo de 2026"  → Intl 로 monthName 추출 (i18n format 에 주입)
  // - ko/ja/zh: 기존 i18n format 그대로 (각 언어별 천체 표기 유지)
  const LOCALE_MAP = { ko: 'ko-KR', en: 'en-US', ja: 'ja-JP', zh: 'zh-CN', es: 'es-ES' };
  const formatMonthName = (year, monthIdx) => {
    try {
      const locale = LOCALE_MAP[i18n.language] || 'en-US';
      return new Intl.DateTimeFormat(locale, { month: 'long' }).format(new Date(year, monthIdx));
    } catch {
      return String(monthIdx + 1);
    }
  };
  const DAYS = t('common.days_short').split('|');
  const resolvedPlaceholder = placeholder || t('form.select_date');
  const parsed = parseISO(value);
  const [open, setOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(parsed.date);
  const [selectedHour, setSelectedHour] = useState(parsed.hour);
  const [selectedMinute, setSelectedMinute] = useState(parsed.minute);

  const now = new Date();
  const [viewYear, setViewYear] = useState(selectedDate ? selectedDate.getFullYear() : now.getFullYear());
  const [viewMonth, setViewMonth] = useState(selectedDate ? selectedDate.getMonth() : now.getMonth());

  const ref = useRef(null);

  // #19: 모바일 viewport 감지 — Navbar 가 dropdown 을 가리는 문제로 인라인 style 강제 적용용
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 640);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // blockHolidays=true (서비스 예약 픽커) → 종일 휴무일을 달력에서 회색·비클릭 처리.
  //   종일 휴무만 차단: specific_date + is_active + 시간대(start/end)·할증(surcharge>0) 아님.
  //   (시간대 휴무·공휴일 할증은 날짜 전체를 막지 않으므로 제외 → 기존 가용성/가격 로직이 처리)
  const [blockedDates, setBlockedDates] = useState(() => new Set());
  useEffect(() => {
    if (!blockHolidays) return undefined;
    let cancelled = false;
    client.get(endpoints.common.holidays)
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : [];
        const s = new Set();
        list.forEach((h) => {
          if (h.specific_date && h.is_active && !(h.start_at && h.end_at) && !(Number(h.surcharge_amount) > 0)) {
            s.add(h.specific_date);
          }
        });
        setBlockedDates(s);
      })
      .catch(() => { /* 조회 실패 시 BE 가 생성 단계에서 차단(우회 안전망) */ });
    return () => { cancelled = true; };
  }, [blockHolidays]);

  // PG 정책 상한일 — maxMonths 지정 시 "오늘 + maxMonths개월" 초과 날짜를 달력에서 비활성화.
  //   handleConfirm 의 alert 가드와 동일 기준(이제 클릭 자체가 막혀 alert 는 사실상 안전망).
  const _now = new Date();
  const maxLimitDate = maxMonths > 0 && !allowPast
    ? new Date(_now.getFullYear(), _now.getMonth() + maxMonths, _now.getDate())
    : null;

  // Sync state when value prop changes externally
  useEffect(() => {
    const p = parseISO(value);
    setSelectedDate(p.date);
    setSelectedHour(p.hour);
    setSelectedMinute(p.minute);
    if (p.date) {
      setViewYear(p.date.getFullYear());
      setViewMonth(p.date.getMonth());
    }
  }, [value]);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handle = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handle = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [open]);


  const prevMonth = useCallback(() => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1); }
    else setViewMonth(viewMonth - 1);
  }, [viewMonth, viewYear]);

  const nextMonth = useCallback(() => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1); }
    else setViewMonth(viewMonth + 1);
  }, [viewMonth, viewYear]);

  const handleDayClick = (dayObj) => {
    if (dayObj.isPast || !dayObj.isCurrentMonth) return;
    /* #109: 당일 클릭 시 selectedDate가 이미 today이면 state change 감지가 안 되어
       시각 피드백이 없다는 피드백이 있음. 항상 새 Date 인스턴스로 세팅하고,
       당일 선택 시 시간도 now+120 버퍼 이후로 자동 보정해서 confirm alert를 예방 */
    const newDate = new Date(dayObj.date.getTime());
    setSelectedDate(newDate);

    const now = new Date();
    const isTodayClick = newDate.getFullYear() === now.getFullYear()
      && newDate.getMonth() === now.getMonth()
      && newDate.getDate() === now.getDate();
    if (isTodayClick && !allowPast) {
      const minTotal = now.getHours() * 60 + now.getMinutes() + 120;
      const selectedTotal = selectedHour * 60 + selectedMinute;
      if (selectedTotal < minTotal) {
        /* 다음 10분 단위로 올림 */
        const ceiled = Math.ceil(minTotal / 10) * 10;
        const h = Math.floor(ceiled / 60);
        const m = ceiled % 60;
        if (h < 24) {
          setSelectedHour(h);
          setSelectedMinute(m);
        }
      }
    }
  };

  const handleConfirm = () => {
    if (!selectedDate) return;
    const now2 = new Date();
    // maxDateTime이 없을 때만 현재 시간 + 2시간 버퍼 체크 (메인 예약 시간용)
    // allowPast=true (어드민 검색 필터) 면 모든 시간/날짜 가드 스킵.
    if (!maxDateTime && !allowPast) {
      if (isSameDay(selectedDate, now2)) {
        const selectedTotal = selectedHour * 60 + selectedMinute;
        const minTotal = now2.getHours() * 60 + now2.getMinutes() + 120;
        if (selectedTotal < minTotal) {
          alert(t('common.alert_same_day_2h'));
          return;
        }
      }
      // 과거 날짜 선택 방지
      const todayStart = new Date(now2.getFullYear(), now2.getMonth(), now2.getDate());
      if (selectedDate < todayStart) {
        alert(t('common.alert_future_date'));
        return;
      }
      // PG 입점 정책 (토스/카카오페이): 결제일 기준 N개월 후까지만 예약 가능
      if (maxMonths > 0) {
        const maxDate = new Date(now2.getFullYear(), now2.getMonth() + maxMonths, now2.getDate());
        if (selectedDate > maxDate) {
          alert(t('common.alert_max_months', { months: maxMonths, defaultValue: `최대 ${maxMonths}개월 후까지 예약 가능합니다.` }));
          return;
        }
      }
    }
    // maxDateTime 제한 (예: 항공편 도착 시간은 차량 이용 시간 10분 전까지만)
    if (maxDateTime) {
      const maxParsed = parseISO(maxDateTime);
      const maxTotal = maxParsed.date.getTime() + maxParsed.hour * 3600000 + maxParsed.minute * 60000;
      const selTotal = selectedDate.getTime() + selectedHour * 3600000 + selectedMinute * 60000;
      if (selTotal > maxTotal) {
        alert(t('common.alert_max_time_format', { time: `${pad(maxParsed.hour)}:${pad(maxParsed.minute)}` }));
        return;
      }
    }
    if (dateOnly) {
      // YYYY-MM-DD 형식만 emit
      const y = selectedDate.getFullYear();
      const m = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const d = String(selectedDate.getDate()).padStart(2, '0');
      onChange(`${y}-${m}-${d}`);
    } else {
      onChange(toISOString(selectedDate, selectedHour, selectedMinute));
    }
    setOpen(false);
  };

  const calendarDays = generateCalendarDays(viewYear, viewMonth, allowPast);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const isSameDay = (a, b) => a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

  const displayValue = value
    ? (dateOnly && parsed.date
        ? t('common.date_full_format', { year: parsed.date.getFullYear(), month: pad(parsed.date.getMonth() + 1), day: pad(parsed.date.getDate()) })
        : formatDisplay(parsed.date, parsed.hour, parsed.minute, t))
    : '';

  return (
    <div ref={ref} className={`relative ${className}`}>
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`w-full h-12 px-4 pr-10 bg-mono-50 border rounded-lg text-[15px] font-semibold sm:text-sm sm:font-medium text-left transition ${
          open ? 'border-orange-500 ring-1 ring-orange-500/20' : 'border-mono-100'
        } ${displayValue ? 'text-mono-900' : 'text-mono-400'}`}
      >
        {displayValue || resolvedPlaceholder}
      </button>
      <Icon
        name="calendar"
        size={16}
        className={`absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none transition-colors ${
          open ? 'text-orange-500' : 'text-mono-400'
        }`}
      />

      {/* Dropdown — #85: viewport overflow 방지를 위해 max-w 추가 (right-aligned 안전 배치)
          #19 (2026-05-08 후속2): 모바일은 portal 로 body 직접 렌더 + fixed top: navbar 아래.
          → input 이 페이지 어디에 있든 dropdown 시작점은 항상 navbar 바로 아래로 통일.
          → max-height: calc(100vh - navbar) + 안 overflow-y-auto (작은 폰 안전망 — 큰 폰
            에서는 컨텐츠 다 들어가서 스크롤 자동 X)
          → 데스크톱(sm:)은 기존 동작 유지 — input 우측 정렬 absolute dropdown. */}
      {open && (
        <div
          style={isMobile ? { top: MOBILE_DROPDOWN_TOP, left: 8, right: 8, maxHeight: `calc(100vh - ${MOBILE_DROPDOWN_TOP + 12}px)`, zIndex: 100 } : undefined}
          className="fixed sm:absolute sm:right-0 sm:top-full sm:left-auto sm:mt-1.5 sm:z-50 bg-white border border-mono-200 rounded-xl shadow-lg overflow-y-auto sm:overflow-hidden animate-dropdown sm:w-[460px] sm:max-w-[calc(100vw-2rem)] sm:max-h-none flex flex-col sm:block"
        >
          {/* #19 근본 픽스: dropdown 을 flex-col 로 만들고
                헤더(flex-shrink-0) + 스크롤 body(flex-1 min-h-0 overflow-y-auto) + footer(flex-shrink-0)
                구조로 분리. 헤더는 절대 잘리지 않고, 컨텐츠 초과 시 body 만 스크롤. */}

          {/* Header row — 월 이동 + 이용 시작 시각 (모바일/데스크탑 공통) */}
          <div className="flex flex-col sm:flex-row border-b border-mono-100 shrink-0 bg-white">
            <div className="w-full sm:flex-1 sm:w-auto flex items-center justify-between px-4 py-3">
              <button type="button" onClick={prevMonth} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-mono-50 transition">
                <Icon name="chevron-left" size={18} className="text-mono-900" />
              </button>
              <span className="text-sm font-bold text-mono-900">
                {t('common.year_month_format', {
                  year: viewYear,
                  month: viewMonth + 1,
                  monthName: formatMonthName(viewYear, viewMonth),
                })}
              </span>
              <button type="button" onClick={nextMonth} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-mono-50 transition">
                <Icon name="chevron-right" size={18} className="text-mono-900" />
              </button>
            </div>
            {!dateOnly && (
              <div className="sm:w-[160px] sm:border-l border-mono-100 flex items-center px-4 py-3">
                <p className="text-sm font-bold text-mono-900">{t('common.usage_start_time')}</p>
              </div>
            )}
          </div>

          {/* Body row — BugHerd #19 후속: 모바일에서 달력 + 시간 wheel + Confirm 모두 한 화면에 보이도록
              내부 스크롤 제거(overflow-visible) + cell/패딩 콤팩트화. */}
          <div className="flex flex-col sm:flex-row sm:flex-none flex-1 min-h-0 overflow-visible">
            {/* Left — Calendar */}
            <div className="w-full sm:flex-1 sm:w-auto">
              <div className="grid grid-cols-7 px-3 pt-1">
                {DAYS.map((d) => (
                  <div key={d} className="h-6 flex items-center justify-center text-2xs font-bold text-mono-400">
                    {d}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7 px-3 pb-1">
                {calendarDays.map((dayObj, idx) => {
                  const isSelected = isSameDay(dayObj.date, selectedDate);
                  const isToday = isSameDay(dayObj.date, today);
                  const ymd = `${dayObj.date.getFullYear()}-${pad(dayObj.date.getMonth() + 1)}-${pad(dayObj.date.getDate())}`;
                  // 과거·타월 + (maxMonths 초과) + (종일 휴무일) → 회색·비클릭
                  const disabled = dayObj.isPast || !dayObj.isCurrentMonth
                    || (maxLimitDate && dayObj.date > maxLimitDate)
                    || blockedDates.has(ymd);
                  return (
                    <button
                      key={idx}
                      type="button"
                      disabled={disabled}
                      onClick={() => handleDayClick(dayObj)}
                      className={`h-8 w-8 sm:h-9 sm:w-9 mx-auto rounded-lg text-2sm font-medium transition
                        ${isSelected ? 'bg-orange-500 text-white font-bold' : ''}
                        ${isToday && !isSelected ? 'bg-orange-50 text-orange-500 font-bold' : ''}
                        ${!isSelected && !isToday && !disabled ? 'text-mono-900 hover:bg-orange-50/50' : ''}
                        ${disabled ? 'text-mono-400/30 cursor-default' : 'cursor-pointer'}
                      `}
                    >
                      {dayObj.day}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Right — Time scroll wheel (dateOnly 모드에서는 숨김) */}
            {!dateOnly && (
              <div className="sm:w-[160px] sm:border-l border-t sm:border-t-0 border-mono-100 flex items-center justify-center gap-1 px-3 py-2 sm:py-3">
                <TimeColumn items={HOURS} value={selectedHour} onChange={setSelectedHour} label={t('common.hour_unit')} />
                <span className="text-xs font-bold text-mono-300 mt-4">:</span>
                <TimeColumn items={MINUTES} value={selectedMinute} onChange={setSelectedMinute} label={t('common.minute_unit')} />
              </div>
            )}
          </div>

          {/* Confirm — shrink-0 로 footer 항상 표시 (#19 구조) */}
          <div className="px-4 py-2 sm:py-3 border-t border-mono-100 shrink-0 bg-white">
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!selectedDate}
              className="w-full h-10 bg-orange-500 text-white text-sm font-bold rounded-lg hover:bg-orange-700 transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {t('common.confirm')}
            </button>
          </div>
        </div>
      )}

      {/* 안내문구 — PG 입점 정책상 2개월 예약 한도 안내 (호출부 옵션). dropdown 은 absolute 라 흐름 영향 없음. */}
      {notice && (
        <p className="text-2xs text-mono-400 mt-1.5 leading-relaxed break-keep">{notice}</p>
      )}
    </div>
  );
}
